import axios from "axios";
import RetryService from "../app/services/RetryService";
import { alertsActions } from "../features/alerts/alertsSlice";
import {
  refreshUserTokens,
  selectAuth,
  toggleReAuth,
} from "../features/auth/authSlice";
import {
  getAvatarClub,
  getAvatarUser,
  initAvatarsIDB,
} from "../features/photos/photosSlice";
import { getPlayers, getSquads } from "../features/squad/squadSlice";
import { getChampionship } from "../features/championship/championshipSlice";
import { getDatepicker } from "../features/datepicker/datepickerSlice";
import { getEvents } from "../features/events/eventsSlice";
import { getSessions } from "../features/sessions/sessionsSlice";
import { getTrashSessions } from "../features/trash/trashSlice";
import {
  getSessionsMarkers,
  getWellnessMarkers,
} from "../features/markers/markersSlice";
import { getTags } from "../features/tags/tagsSlice";
import { getChoices } from "../features/choices/choicesSlice";
import { getMods } from "../features/mods/modsSlice";
import { getTables } from "../features/tables/tablesSlice";
import { getDataStudios } from "../features/dataStudio/dataStudioSlice";
import { getPlayerCompletion } from "../features/questionnaire/questionnaireSlice";
import { selectUser } from "../features/user/userSlice";
import { checkPushNotifSub } from "../features/pushNotif/pushNotifSlice";
import { statsCollectiveActions } from "../features/statsCollective/statsCollectiveSlice";
import { getStliteApps } from "../features/stlite/stliteSlice";
import { selectLang } from "../features/lang/langSlice";
import {
  appVersion,
  getBrowserName,
  getOsName,
  getPlatformType,
  isPWAInstalled,
} from "./utils";
import { isNotificationSupported } from "../features/pushNotif/utils";

const APIS = {
  PRODUCTION: "https://api.python-sports.com/",
  STAGING: "https://api-staging.python-sports.com/",
  DEV: "https://api-dev.python-sports.com/",
};

export const isProd = ["pysports.web.app", "python-sports.com"].includes(
  window.location.hostname
);

export const isStaging = window.location.hostname.includes("staging");

export const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  window.location.hostname === "[::1]" ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  ) ||
  window.location.hostname.includes("192.168")
);

export const backendUrl = isProd
  ? APIS.PRODUCTION
  : import.meta.env.DEV
    ? APIS.DEV
    : APIS.STAGING;

// https://redux.js.org/faq/code-structure#how-can-i-use-the-redux-store-in-non-component-files
let store;

export const injectStore = (_store) => {
  store = _store;
};

export const api = axios.create({
  baseURL: "", // sera init au login
});

// appelée au login
export const initApiInstance = (url, accessToken) => {
  const { proxy, token } = selectAuth(store.getState());
  const lang = selectLang(store.getState());

  // base url
  api.defaults.baseURL = url || proxy || "";

  // token access
  api.defaults.headers.Authorization = `Bearer ${
    accessToken || token.access || ""
  }`;

  // lang
  api.defaults.headers["Accept-Language"] = lang;
  api.defaults.headers["X-App-Language"] = lang;

  // user infos
  api.defaults.headers["X-App-Version"] = appVersion;
  api.defaults.headers["X-Platform"] = getPlatformType();
  api.defaults.headers["X-OS"] = getOsName();
  api.defaults.headers["X-Browser"] = getBrowserName();
  api.defaults.headers["X-PWA"] = isPWAInstalled() ? "true" : "false";
  // api.defaults.headers["X-Push-Notif"] =
  //   "Notification" in window ? Notification.permission : "unsupported";

  return Promise.resolve();
};

// Avant chaque envoi de requête, on ajoute des headers
api.interceptors.request.use(
  (config) => {
    const accessToken = selectAuth(store.getState()).token.access;
    // const accessToken = localStorage.getItem("accessToken");
    const lang = selectLang(store.getState());

    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;

    config.headers["Accept-Language"] = lang;
    config.headers["X-App-Language"] = lang;

    return config;
  },
  (error) => Promise.reject(error)
);

// Ici on regarde les tokens pour voir s'ils ont expirés ou pas
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const unauthorized = error.response.status === 401;
    // Mauvais identifiants renvoie une erreur 401
    const refreshTokenHasExpired = originalRequest.url === "token/refresh/";

    // Si unauthorized cela veut dire que un des 2 tokens a expiré
    if (unauthorized) {
      // Si ce code d'erreur concerne l'endpoint de refresh, cela veut dire
      // que nous avons essayé de renouveller l'access, mais que le refresh a expiré,
      // nous devons donc soit déconnecter l'user, soit ouvrir un dialog de re auth
      if (refreshTokenHasExpired) {
        const isReAuthClose = selectAuth(store.getState()).openReAuth === false;
        const isUserLogged =
          selectUser(store.getState()).status === "fulfilled";

        if (isReAuthClose && isUserLogged) {
          await store.dispatch(toggleReAuth(true));
        }

        // sinon on doit logout mais c'est fait dans get user/

        return Promise.reject(error);
      } else {
        // Si ce code ne concerne pas l'endpoint de refresh
        // cela veut dire que l'access a expiré, et que le refresh n'a pas encore été évalué
        // On doit essayer de renouveler l'access token
        // On envoie donc à api/token/refresh/ le refresh token
        // Mais s'il a lui aussi expiré, cet interceptor déconnectera grâce au
        // block précédent car nous aurons une erreur 401

        // on set retry à true pour éviter un cycle sans fin de réponses 401
        // Puisque dans ce block, nous renvoyons une requête et non une erreur,
        // l'interceptor fera
        // son job et renverra une erreur 401 s'il y en a une !

        // Le but de ce block est de réessayer la requête échouée à cause de
        // l'expiration de l'access
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          await store.dispatch(refreshUserTokens());

          // BUG AXIOS open 11 nov 2022 : https://github.com/axios/axios/issues/5143
          // SOLUTION : https://stackoverflow.com/questions/74166648/setrequestheader-fails-to-execute-with-source-code-as-a-header-value-axios-an/74308583#74308583
          return api({
            ...originalRequest,
            headers: { ...originalRequest.headers },
            sent: true,
          });
        }
      }
    }

    // 409 : la requête est suspendue en attendant une action de l'user
    // Même principe que le refresh de token 401 : on retourne une Promise en attente
    // au lieu de rejeter — la chaîne .then()/.catch() du composant est préservée
    // et s'exécutera naturellement après le retry ou le cancel.
    if (error.response.status === 409) {
      const actions = error.response.data?.actions;

      // Le 409 provient d'un retry échoué.
      // On laisse handleRetry gérer l'erreur afin de garder le drawer ouvert.
      if (originalRequest._retry409) {
        // Le retry a échoué avec un nouveau 409.
        // On force la fermeture/réouverture du drawer
        // pour afficher les nouvelles actions.
        store.dispatch(alertsActions.clearActions());

        // pour tester la mise à jour des actions si encore erreur 409
        // const actions = {
        //   message: "message",
        //   buttons: [{ label: "button", qparam: { key: "key", value: "value" } }],
        // };

        setTimeout(() => {
          store.dispatch(alertsActions.setActions(actions));
        }, 200);

        return Promise.reject(error);
      }

      originalRequest._retry409 = true;

      store.dispatch(alertsActions.setActions(actions));

      return new Promise((resolve, reject) => {
        RetryService.register({ resolve, reject, config: originalRequest });
      });
    }

    return Promise.reject(error);
  }
);

const getClubSetup = (isPlayer, isCoach) => {
  return async (dispatch, getState) => {
    const requests = [
      { slice: "squad", reqId: "getSquads", back: "teams", simple: true },
      {
        slice: "choices",
        reqId: "get",
        back: "squad_configuration",
        simple: true,
      },
      { slice: "tables", reqId: "get", back: "tables", simple: true },
      { slice: "tags", reqId: "get", back: "tags", simple: true },
      {
        slice: "trash",
        reqId: "getSessions",
        back: "deleted_session_types",
        simple: true,
      },
      {
        slice: "championship",
        reqId: "get",
        back: "competition_teams",
        simple: true,
      },
      // simple = false pour les requêtes nécessitants une config. supplémentaire quand fulfilled
      { slice: "sessions", reqId: "get", back: "session_types", simple: false },
      { slice: "mods", reqId: "get", back: "modules", simple: false },
      { slice: "dataStudio", reqId: "get", back: "datastudios", simple: false },
    ];

    // si c'est un joueur on enlève les requêtes des tables et des dataStudios
    if (isPlayer) {
      const tablesRequestIndex = requests.findIndex(
        (item) => item.slice === "tables"
      );
      requests.splice(tablesRequestIndex, 1);

      const dataStudiosRequestIndex = requests.findIndex(
        (item) => item.slice === "dataStudio"
      );
      requests.splice(dataStudiosRequestIndex, 1);
    }

    // on envoie tous les chargements
    requests.forEach((d) =>
      dispatch({ type: `${d.slice}/${d.reqId}/pending` })
    );

    return api
      .get("club-setup/")
      .then((res) => {
        // on fulfill les requêtes simple
        requests
          .filter(({ simple }) => simple)
          .forEach((d) =>
            dispatch({
              type: `${d.slice}/${d.reqId}/fulfilled`,
              payload: res.data[d.back],
            })
          );

        // requêtes nécessitants une config. supplémentaire
        const logoUrl = res.data["competition_teams"].find(
          (d) => d.app_team
        ).logo;

        dispatch(getAvatarClub(logoUrl));

        dispatch({
          type: "sessions/get/fulfilled",
          payload: res.data["session_types"].filter((d) => d.id !== 1),
        });

        const userPk = selectUser(getState()).data.pk;

        // init dataVizType
        dispatch(
          statsCollectiveActions.setDataVizType({
            userPk,
            id:
              localStorage.getItem(`user${userPk}_coll_dataviz_type_v2`) ||
              "dashboard",
          })
        );

        // const allColl = [
        //   3, 4, 207, 208, 209, 210, 211, 212, 213, 214, 215, 216, 219, 220, 221,
        //   222, 223, 224, 225, 226, 227, 228, 229, 230, 231, 1000,
        // ];
        // const allIndiv = [
        //   0, 1, 2, 3, 4, 5, 207, 208, 215, 216, 217, 218, 219, 221,
        // ];
        // const fakeMods = {
        //   coll: allColl.map((d, i) => ({ id: i, graph_id: d, order_id: i })),
        //   indiv: allIndiv.map((d, i) => ({ id: i, graph_id: d, order_id: i })),
        // };

        dispatch({
          type: "mods/get/fulfilled",
          payload: {
            userPk,
            mods: res.data["modules"],
            // mods: fakeMods,
          },
        });

        if (isCoach) {
          dispatch({
            type: "dataStudio/get/fulfilled",
            payload: { userPk, dataStudios: res.data["datastudios"] },
          });
        }
      })
      .catch((error) => {
        console.error("error in getClubSetup", error?.response);
        requests.forEach((d) =>
          dispatch({
            type: `${d.slice}/${d.reqId}/rejected`,
            payload: error?.response?.data,
          })
        );
      });
  };
};

export const loadCoreData = ({
  group = null,
  isCoach = group === "Coach",
  isPlayer = group === "Joueurs",
  pk = null,
  ignoreCondition = false, // if true, it means its a refresh via the PDR
}) => {
  return async (dispatch) => {
    if (isNotificationSupported) {
      dispatch(checkPushNotifSub());
    }

    initAvatarsIDB().then(() => {
      dispatch(getPlayers({ ignoreCondition }));
      dispatch(getAvatarUser());

      if (ignoreCondition) {
        // Refresh with PDR
        dispatch(getChampionship({ ignoreCondition })); // dans getClubSetup
      } else {
        // Init
        dispatch(getClubSetup(isPlayer, isCoach));
      }
    });

    // Init & Refresh with PDR
    dispatch(getDatepicker({ ignoreCondition }));
    dispatch(getEvents({ ignoreCondition }));
    dispatch(getSessionsMarkers({ ignoreCondition }));
    dispatch(getWellnessMarkers({ ignoreCondition }));

    // Init
    // Refresh = dans le PDR
    dispatch(getStliteApps({ ignoreCondition: false }));

    if (ignoreCondition) {
      // Refresh with PDR
      dispatch(getSessions({ ignoreCondition })); // dans getClubSetup
      dispatch(getTrashSessions({ ignoreCondition })); // dans getClubSetup
      dispatch(getTags({ ignoreCondition })); // dans getClubSetup
      dispatch(getChoices({ ignoreCondition })); // dans getClubSetup (squad-configuration)
      dispatch(getSquads({ ignoreCondition })); // dans getClubSetup
      dispatch(getMods({ ignoreCondition })); // dans getClubSetup
      if (isCoach) {
        dispatch(getTables({ ignoreCondition })); // dans getClubSetup
        dispatch(getDataStudios({ ignoreCondition })); // dans getClubSetup
      }
    }

    if (isPlayer) {
      dispatch(getPlayerCompletion({ pk }));
    }
  };
};
